import { useQuery } from "react-query";
import { Activity, Boxes, Gauge, ListTree, Users, Zap } from "lucide-react";
import { Card } from "../../../ui/card";
import { saasApi } from "../shared/saasApi";
import { CATEGORY_COLORS, categoryLabel, formatNumber, humanize, PROVIDERS, RISK_COLORS } from "../shared/saasFormat";
import { EventCategory, RiskLevel } from "../shared/saasTypes";
import { ActivityTimelineChart } from "../shared/SaasCharts";
import { BreakdownBars, HeroStat, SectionCard } from "../shared/SaasUi";
import { singleConnectionId, WorkspaceContext } from "./workspaceContext";

export default function ActivityTab({ context }: { context: WorkspaceContext }) {
    const meta = PROVIDERS[context.provider];
    const activityQuery = useQuery(
        ["saas-activity", context.provider, context.tenantName, context.connectionId, context.range],
        () => saasApi.activity(context.provider, context.tenantName, context.connectionId, context.range),
        { keepPreviousData: true },
    );
    const activity = activityQuery.data;
    const connectionForIdentity = singleConnectionId(context);

    if (!activity) {
        return <Card className="cloud-card-surface rounded-2xl h-96 animate-pulse" />;
    }

    return (
        <div className="space-y-6">
            <div className="cs-hero">
                <HeroStat icon={<Activity />} value={formatNumber(activity.eventCount)} label="Events" />
                <HeroStat icon={<Zap />} tone="red" value={formatNumber(activity.highRiskCount)} label="High-risk events"
                          onClick={() => context.openEvents({ minRiskLevel: "HIGH" })} />
                <HeroStat icon={<Users />} tone="green" value={formatNumber(activity.activeActors)} label="Active identities" />
                <HeroStat icon={<Boxes />} tone="amber" value={formatNumber(activity.activeResources)} label={`Active ${meta.resourceLabel.toLowerCase()}`} />
            </div>

            <SectionCard title="Activity over time" subtitle={`Grouped by ${activity.interval}, stacked by category`} icon={<Activity className="w-4 h-4" />}>
                <ActivityTimelineChart activity={activity} height={320} />
            </SectionCard>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <SectionCard title="By category" icon={<ListTree className="w-4 h-4" />} className="h-[420px]" scroll>
                    <BreakdownBars
                        items={activity.categories}
                        labelFor={(item) => categoryLabel(item.key)}
                        colorFor={(item) => CATEGORY_COLORS[item.key] ?? "hsl(var(--primary))"}
                        showDistinct="identities"
                        onSelect={(item) => context.openEvents({ category: item.key as EventCategory })}
                    />
                </SectionCard>
                <SectionCard title="By risk level" icon={<Gauge className="w-4 h-4" />} className="h-[420px]" scroll>
                    <BreakdownBars
                        items={activity.riskLevels}
                        labelFor={(item) => humanize(item.key)}
                        colorFor={(item) => RISK_COLORS[item.key as RiskLevel] ?? "hsl(var(--primary))"}
                        showDistinct="identities"
                        onSelect={(item) => context.openEvents({ minRiskLevel: item.key as RiskLevel })}
                    />
                </SectionCard>
                <SectionCard title="Top actions" icon={<Zap className="w-4 h-4" />} className="h-[420px]" scroll>
                    <BreakdownBars items={activity.actions} showDistinct="identities" onSelect={(item) => context.openEvents({ action: item.key })} />
                </SectionCard>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <SectionCard title="Top identities" icon={<Users className="w-4 h-4" />} className="h-[420px]" scroll>
                    <BreakdownBars
                        items={activity.actors}
                        showDistinct="resources"
                        onSelect={connectionForIdentity ? (item) => context.openIdentity(connectionForIdentity, item.key) : (item) => context.openEvents({ actor: item.key })}
                    />
                </SectionCard>
                <SectionCard title={`Top ${meta.resourceLabel.toLowerCase()}`} icon={<Boxes className="w-4 h-4" />} className="h-[420px]" scroll>
                    <BreakdownBars items={activity.resources} showDistinct="identities" onSelect={(item) => context.openEvents({ resource: item.key })} />
                </SectionCard>
            </div>
        </div>
    );
}
